// @flow

import logger from '../../app/logger';
import { constructOptions } from '../connection/actions.any';
import MeetHourJS, { MHConnectionEvents } from '../lib-meet-hour';

import {
    configureConferenceInfo,
    declareWaiting,
    subscribeConferenceInfo
} from './conferenceInfoClient';
import { fetchConferenceInfo } from './roomInfo';

/**
 * Normalizes a room name for cache keying.
 *
 * @param {?string} room - Room name to normalize.
 * @returns {string}
 */
function toSafeRoomName(room: ?string): string {
    if (!room) {
        return '';
    }

    try {
        return decodeURIComponent(room)
            .trim()
            .toLowerCase();
    } catch {
        return String(room)
            .trim()
            .toLowerCase();
    }
}

/**
 * In-memory cache of the latest conference-info by room name.
 * Allows instant synchronous retrieval on mount or remount.
 */
const conferenceInfoCache: Map<string, Object> = new Map();

/**
 * Returns any cached conference-info for the specified room.
 *
 * @param {string} roomName - The room name.
 * @returns {?Object}
 */
export function getCachedConferenceInfo(roomName: ?string): ?Object {
    if (!roomName) {
        return undefined;
    }

    return conferenceInfoCache.get(toSafeRoomName(roomName));
}

/**
 * Primes conference-info via HTTP as early as possible (e.g. on SET_ROOM).
 *
 * @param {string} roomName - The room name.
 * @returns {Function} Redux thunk.
 */
export function primeConferenceInfo(roomName: ?string) {
    return async (dispatch: Function, getState: Function): Promise<?Object> => {
        const safeRoomName = toSafeRoomName(roomName);

        if (!safeRoomName) {
            return null;
        }

        const state = getState();

        const params = {
            roomName: safeRoomName,
            hosts: state['features/base/config']?.hosts,
            origin: state['features/base/connection']?.locationURL?.origin,
            token: state['features/base/jwt']?.jwt
        };

        const info = await fetchConferenceInfo(params);

        if (info) {
            conferenceInfoCache.set(safeRoomName, info);
        }

        return info;
    };
}

/**
 * The one soft XMPP session for this page.
 *
 * "Soft" means connected but not joined: it authenticates the user (JWT for a
 * moderator, anonymous for a guest) and nothing else. Deliberately built outside
 * the redux connect action, because that action dispatches CONNECTION_ESTABLISHED
 * and the base/conference middleware answers that by calling createConference()
 * on native - which would join the meeting the moment a guest opened the prejoin
 * screen. Nothing here dispatches, so nothing joins.
 *
 * Kept alive after the watching component unmounts so the real join can adopt it
 * rather than paying for a second connect. The page unload tears it down.
 */
let softConnection = null;

/**
 * In-flight connect, so two components mounting together share one session
 * instead of opening two.
 */
let softConnectionPromise = null;

/**
 * Poll period used when a caller does not pick one.
 */
const DEFAULT_FALLBACK_INTERVAL = 10000;

/**
 * How many fallback ticks to wait between attempts to open a fresh XMPP session.
 * Re-subscribing on an existing session is cheap and happens every tick; opening
 * a connection is not, and on a network that blocks it, retrying every tick is
 * just a stream of doomed connects.
 */
const RECONNECT_EVERY_TICKS = 10;

/**
 * Returns the soft connection, opening one if this is the first caller.
 *
 * @param {Function} getState - Redux getState.
 * @returns {Promise<?Object>} Null when the connection could not be opened.
 */
function getSoftConnection(getState: Function): Promise<?Object> {
    if (softConnection) {
        return Promise.resolve(softConnection);
    }

    if (softConnectionPromise) {
        return softConnectionPromise;
    }

    softConnectionPromise = openSoftConnection(getState, false);

    return softConnectionPromise;
}

/**
 * Opens the soft connection over the configured transport.
 *
 * A websocket that cannot be opened (a certificate that does not cover the
 * websocket host, a proxy that strips the upgrade) is retried once over BOSH.
 * The main connection makes the same switch, but only after it has failed itself,
 * which is after prejoin; without the retry the waiting guest's declaration never
 * reaches the server and the moderator's waiting list stays empty.
 *
 * @param {Function} getState - Redux getState.
 * @param {boolean} preferBosh - Whether to skip the websocket.
 * @returns {Promise<?Object>} Null when the connection could not be opened.
 */
function openSoftConnection(getState: Function, preferBosh: boolean): Promise<?Object> {
    return new Promise(resolve => {
        let connection;
        let options;

        try {
            const state = getState();

            options = constructOptions(preferBosh ? {
                ...state,
                'features/base/config': {
                    ...state['features/base/config'],
                    preferBosh: true
                }
            } : state);
            const { jwt } = state['features/base/jwt'];

            connection = new MeetHourJS.MHConnection(options.appId, jwt, options);
        } catch (e) {
            logger.warn(`soft connection could not be constructed: ${e.message}`);
            softConnectionPromise = null;

            return resolve(null);
        }

        const cleanup = () => {
            connection.removeEventListener(MHConnectionEvents.CONNECTION_ESTABLISHED, onEstablished);
            connection.removeEventListener(MHConnectionEvents.CONNECTION_FAILED, onFailed);
        };

        /**
         * Adopts the connection once the session is up.
         *
         * @returns {void}
         */
        function onEstablished() {
            cleanup();
            softConnection = connection;
            softConnectionPromise = null;
            resolve(connection);
        }

        /**
         * Resolves null so callers fall back rather than hang.
         *
         * @param {...*} args - Failure details from the library.
         * @returns {void}
         */
        function onFailed(...args) {
            cleanup();
            logger.warn(`soft connection failed: ${args.join(' ')}`);

            if (!preferBosh && options.bosh && /^wss?:/i.test(options.serviceUrl || '')) {
                resolve(openSoftConnection(getState, true));

                return;
            }

            softConnectionPromise = null;
            resolve(null);
        }

        connection.addEventListener(MHConnectionEvents.CONNECTION_ESTABLISHED, onEstablished);
        connection.addEventListener(MHConnectionEvents.CONNECTION_FAILED, onFailed);

        // No `name` on purpose. Passing a room name makes MHConnection send a
        // conference request to jicofo, which is the first step of joining. This
        // must stay a bare c2s session.
        connection.connect({});
    });
}

/**
 * Returns the live soft connection, if one has been established.
 *
 * Exposed so the real join can reuse the session opened at prejoin instead of
 * connecting a second time.
 *
 * @returns {?Object}
 */
export function getEstablishedSoftConnection(): ?Object {
    return softConnection;
}

/**
 * Forgets the soft connection without closing it.
 *
 * Called once the join path has adopted the session, so nothing here disconnects
 * a connection the conference is now using.
 *
 * @returns {void}
 */
export function releaseSoftConnection() {
    softConnection = null;
    softConnectionPromise = null;
}

/**
 * Sends a moderator-only room setting over the pre-join session.
 *
 * Uses the same soft connection the watcher already holds - it is the session
 * the server checks the moderator claim on - and opens one only if nothing has
 * yet.
 *
 * @param {string} roomName - Backend-safe room name.
 * @param {Object} attributes - Attributes for the configure element.
 * @returns {Function} Thunk resolving to whether the server accepted it.
 */
export function configureConference(roomName: string, attributes: Object) {
    return async (dispatch: Function, getState: Function): Promise<boolean> => {
        const connection = await getSoftConnection(getState);

        if (!connection) {
            return false;
        }

        return configureConferenceInfo(connection, roomName, attributes);
    };
}

/**
 * The "I am waiting for this meeting to start" declaration, per room.
 *
 * Held here rather than only sent once, because the moment a screen decides it
 * is waiting is not the moment there is a session to say so on: the soft
 * connection is still being opened, and a send at that point is silently
 * dropped. It is also lost on a reconnect, since the server forgets a session's
 * subscriptions when it unbinds. Keeping the declaration means every subscribe -
 * the first one and every one after a reconnect - carries the current answer.
 */
const waitingDeclarations = new Map();

/**
 * Announces, or withdraws, "I am waiting for this meeting to start".
 *
 * @param {string} roomName - Backend-safe room name.
 * @param {boolean} waiting - Whether this client is waiting to be let in.
 * @param {string} [displayName] - Name to show the moderator.
 * @returns {Function}
 */
export function declareConferenceWaiting(roomName: string, waiting: boolean, displayName?: string) {
    return () => {
        if (!roomName) {
            return;
        }

        if (waiting) {
            waitingDeclarations.set(roomName, {
                displayName,
                waiting: true
            });
        } else {
            waitingDeclarations.delete(roomName);
        }

        const connection = getEstablishedSoftConnection();

        if (connection) {
            declareWaiting(connection, roomName, waiting, displayName);
        } else {
            // Not an error: the subscription that is still being set up will
            // carry this. Logged because "the moderator sees nobody waiting" is
            // otherwise invisible from here.
            logger.debug(`waiting=${String(waiting)} for ${roomName} deferred to the subscription`);
        }
    };
}

/**
 * Watches a conference and reports every change.
 *
 * This replaces the polling that used to drive the prejoin and waiting screens.
 * Those screens ran timers - a setInterval, a ReactInterval, and on mobile a
 * setTimeout that rescheduled itself with no cleanup path at all - which kept
 * firing after unmount and made the guest's join latency a function of the poll
 * period. Here the server pushes, so the wait ends in one round trip and the only
 * thing to clean up is a single unsubscribe.
 *
 * When the push subscription cannot be established - a firewall blocking both
 * websocket and BOSH, or a deployment without mod_conference_info - this falls
 * back to polling over plain HTTP so the screen keeps working. That fallback
 * timer is owned by the same cleanup function as everything else and is guarded
 * by a disposed flag, so unlike the timers this replaced it cannot outlive the
 * screen. It also re-attempts the subscription periodically and drops the timer
 * the moment push becomes available.
 *
 * @param {string} roomName - Backend-safe room name.
 * @param {Function} onUpdate - Called with each ConferenceInfo received.
 * @param {Object} options - Tuning.
 * @param {number} options.fallbackInterval - How often to poll when pushing is
 * impossible. Pick from what the screen does with the value: a join gate wants
 * seconds, a count display does not.
 * @param {Function} options.backendFallback - Optional last resort, used only
 * when even the HTTP endpoint cannot answer. Resolves a ConferenceInfo or null.
 * @returns {Function} Thunk resolving to a cleanup function.
 */
export function watchConferenceInfo(
        roomName: string,
        onUpdate: Function,
        { fallbackInterval = DEFAULT_FALLBACK_INTERVAL, backendFallback }: Object = {}
) {
    return async (dispatch: Function, getState: Function): Promise<Function> => {
        let disposed = false;
        let unsubscribe = null;
        let connection = null;
        let fallbackTimer = null;
        let ticks = 0;

        const safeKey = toSafeRoomName(roomName);

        const deliver = info => {
            if (!disposed && info) {
                if (safeKey) {
                    conferenceInfoCache.set(safeKey, info);
                }
                onUpdate(info);
            }
        };

        const cached = safeKey ? conferenceInfoCache.get(safeKey) : undefined;

        if (cached) {
            deliver(cached);
        }

        // Native has no window to read an origin from and no global config, so
        // the HTTP fallback is given both explicitly. Without this, tier two
        // would silently do nothing on mobile - the exact platform most likely
        // to be on a restricted network.
        const httpParams = () => {
            const state = getState();

            return {
                roomName,
                hosts: state['features/base/config']?.hosts,
                origin: state['features/base/connection']?.locationURL?.origin,
                token: state['features/base/jwt']?.jwt
            };
        };

        // A dropped websocket is re-established by the library itself. Re-running
        // the subscription on that event is what keeps this correct without a
        // timer: the server forgets subscriptions when the session unbinds, so
        // the new session has to re-register.
        const onReestablished = async () => {
            if (disposed) {
                return;
            }

            // The library reuses the same Strophe connection across a reconnect,
            // so the previous stanza handler is still installed. Drop it first or
            // every reconnect doubles the number of times onUpdate fires.
            unsubscribe?.();
            unsubscribe = await subscribeConferenceInfo(
                connection, roomName, deliver, waitingDeclarations.get(roomName));
        };

        let listening = false;

        const attach = async () => {
            connection = await getSoftConnection(getState);

            if (disposed || !connection) {
                return false;
            }

            unsubscribe = await subscribeConferenceInfo(
                connection, roomName, deliver, waitingDeclarations.get(roomName));

            if (!unsubscribe) {
                return false;
            }

            // attach() runs again on every fallback retry, so guard this or each
            // retry leaves another listener behind on the shared connection.
            if (!listening) {
                connection.addEventListener(
                    MHConnectionEvents.CONNECTION_ESTABLISHED, onReestablished);
                listening = true;
            }

            return true;
        };

        /**
         * Reads the current state over HTTP, dropping to the caller's backend
         * path only when the Prosody endpoint cannot answer either.
         *
         * @returns {Promise<void>}
         */
        const pollOnce = async () => {
            const info = await fetchConferenceInfo(httpParams());

            if (info) {
                deliver(info);

                return;
            }

            if (backendFallback) {
                const fallbackInfo = await backendFallback();

                if (fallbackInfo) {
                    deliver(fallbackInfo);
                }
            }
        };

        /**
         * Runs the HTTP fallback loop.
         *
         * Chained timeouts rather than setInterval so a slow request cannot
         * stack up behind itself on the sort of degraded network that put us on
         * this path in the first place.
         *
         * @returns {void}
         */
        const scheduleFallback = () => {
            if (disposed) {
                return;
            }

            fallbackTimer = setTimeout(async () => {
                if (disposed) {
                    return;
                }

                // Push is the better answer whenever it becomes possible again,
                // so keep trying for it. Succeeding ends the polling entirely.
                //
                // When a session already exists this costs one IQ and is worth
                // doing every tick. When there is none, retrying means opening a
                // whole connection, which on the blocked network that put us
                // here would mean a doomed connect attempt every few seconds -
                // so that is spaced out instead.
                ticks += 1;

                const haveSession = Boolean(getEstablishedSoftConnection());

                if ((haveSession || ticks % RECONNECT_EVERY_TICKS === 0) && await attach()) {
                    fallbackTimer = null;

                    return;
                }

                await pollOnce();
                scheduleFallback();
            }, fallbackInterval);
        };

        // Prime the initial conference-info immediately via HTTP (fast: ~30-80ms),
        // rather than waiting seconds for XMPP WebSocket connection setup, TLS,
        // SASL authentication, and Strophe IQ round-trips.
        // attach() runs concurrently in the background to establish push updates.
        pollOnce();

        const subscribed = await attach();

        if (!subscribed && !disposed) {
            logger.warn(
                `conference-info push unavailable for ${roomName}; `
                + `falling back to HTTP every ${fallbackInterval}ms`);

            scheduleFallback();
        }

        return () => {
            disposed = true;

            clearTimeout(fallbackTimer);
            fallbackTimer = null;

            if (connection) {
                connection.removeEventListener(
                    MHConnectionEvents.CONNECTION_ESTABLISHED, onReestablished);
            }

            if (unsubscribe) {
                unsubscribe();
                unsubscribe = null;
            }
        };
    };
}
